import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Mail, Newspaper, Globe, MessageSquare, Check as CheckIconLucide, X as XIconLucide, Filter as FilterIcon } from 'lucide-react';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { DCXPromotionApis } from 'apis/DCXPromotionAPI';
import type { DcxPromotionStatus, DcxEnvironmentRef } from 'types/apis/dcxPromotionInterfaces';
import { projectEnvironmentToAppEnv, isPromotableAppEnvironment } from 'features/contentManagement/mediaContent/mediaEnvMapping';
import { dashboardUsageDetailParts, resolveFeatureDisplayName, titleCaseSegment, withFuelLabel } from 'utils/feature-id';
import studioStyles from '../environments/StudioModal.module.scss';
import { StudioButton } from './StudioButton';

type Presence = 'Present' | 'Not present' | 'Modified';

const STATUS_TO_PRESENCE: Record<DcxPromotionStatus, Presence> = {
  PRESENT: 'Present',
  NOT_PRESENT: 'Not present',
  MODIFIED: 'Modified',
};

/** …and back, for the bundle payload — `status` is what picks create vs update server-side. */
const PRESENCE_TO_STATUS: Record<Presence, DcxPromotionStatus> = {
  Present: 'PRESENT',
  'Not present': 'NOT_PRESENT',
  Modified: 'MODIFIED',
};

/** One environment being compared/promoted into — 1 entry for a direct-write target
 *  (uat/dev/productqa), 2 entries (Non-Prod + Prod) when the target is stage-only. */
type TargetInfo = {
  id: string;
  label: string;
  /** Env type (DEV / UAT / NON-PROD / PROD) — what the compact column chips show. */
  envType: string;
  directWrite: boolean;
  envKind: 'nonprod' | 'prod' | 'direct';
};

/** Classify each resolved target env (from a compare response's targetEnvironments) into
 *  direct-write (uat/dev/productqa) vs stage-only (nonprod/prod), reusing the same
 *  classifier config_tools/media-content promotion already uses elsewhere in this app. */
function mapTargetEnvironments(envs: DcxEnvironmentRef[]): TargetInfo[] {
  return envs.map((e) => {
    const appEnv = projectEnvironmentToAppEnv({ id: String(e.id), name: e.environmentName, stack_name: '', type: e.type });
    const directWrite = appEnv ? isPromotableAppEnvironment(appEnv) : false;
    const envKind: TargetInfo['envKind'] = directWrite ? 'direct' : appEnv === 'prod' ? 'prod' : 'nonprod';
    return {
      id: String(e.id),
      label: e.environmentName ? e.environmentName.toUpperCase() : e.type || String(e.id),
      envType: (e.type || e.environmentName || String(e.id)).toUpperCase(),
      directWrite,
      envKind,
    };
  });
}

type AttributeTargetResult = {
  targetId: string;
  value: string | null;
  userAttributeEvaluatorType: string | null;
  status: Presence;
  staged: boolean;
  stagedValue: string | null;
};

type AttributeRow = {
  attributeId: string;
  sourceValue: string | null;
  userAttributeEvaluatorType: string | null;
  /** Full source object from the compare response — carries entityName/attributeName/description
   *  needed by the direct-write (Sync) update, without hand-modeling every passthrough field. */
  sourceRaw: Record<string, any> | null;
  /** One result per entry in the `targets` prop, same order. */
  targets: AttributeTargetResult[];
};

type ClusterTargetResult = {
  targetId: string;
  value: string | null;
  status: Presence;
  staged: boolean;
  stagedValue: string | null;
  /** The experiment this cluster sits under in that target env — the pool a newly created
   *  cluster picks from, since experiments are per environment. */
  experimentId: string | null;
  experimentName: string | null;
  /** This target's existing clusterId — required by the direct-write update, only present
   *  when status is PRESENT or MODIFIED (NOT_PRESENT has no target cluster to update yet). */
  clusterId: string | null;
};

type ClusterRow = {
  clusterReference: string;
  clusterName: string;
  description: string | null;
  /** PHYSICAL / VIRTUAL, from the payload's `clusterCategory`. */
  clusterCategory: string | null;
  sourceValue: string | null;
  targets: ClusterTargetResult[];
};

type PromotionBundleItem =
  | { kind: 'attribute'; row: AttributeRow; target: AttributeTargetResult }
  | { kind: 'cluster'; row: ClusterRow; target: ClusterTargetResult }
  | { kind: 'sheet'; row: SheetRow; target: SheetTargetResult };

type SheetTargetResult = {
  targetId: string;
  resolution: string | null;
  status: Presence;
  position: number | null;
  mandatory: boolean | null;
};

type SheetRow = {
  featureId: string;
  displayName: string | null;
  clusterReference: string;
  product: string;
  fuelType: string | null;
  level: string;
  /** DASHBOARD only — `metadata.mappedPage`, the page a row belongs to. Drives the
   *  page-then-features arrangement, same as TemplatePersonalizationPage's grid. */
  mappedPage: string | null;
  sourceResolution: string | null;
  sourcePosition: number | null;
  sourceMandatory: boolean | null;
  targets: SheetTargetResult[];
};

const cellSx = {
  p: 1,
  verticalAlign: 'middle',
  borderTop: '1px solid',
  borderColor: 'divider',
  textAlign: 'left',
} as const;

const headSx = {
  p: 1,
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
} as const;

/**
 * Row states from the Studio compare design: a purple inset ring (plus the faintest purple
 * wash) while anything on the row is staged, amber only for drift that isn't staged yet.
 */
const ROW_TINT = {
  staged: 'rgba(147,51,234,0.04)',
  attention: 'rgba(245,158,11,0.06)',
} as const;

const rowStateSx = (staged: boolean, attention: boolean) => ({
  bgcolor: staged ? ROW_TINT.staged : attention ? ROW_TINT.attention : 'transparent',
  ...(staged ? { outline: '1px solid #9333ea', outlineOffset: '-1px' } : null),
  '&:hover': { bgcolor: 'action.hover' },
});

/**
 * Frozen left column, so the module/feature name stays put while the cluster columns scroll.
 *
 * The cell needs an OPAQUE background of its own — the row tints above are semi-transparent, so
 * inheriting one would let the scrolled-under cells show through. Paper is laid down first and
 * the tint painted over it as a gradient, which keeps the frozen cell the same colour as the
 * rest of its row instead of punching a white hole in a staged/attention row.
 */
/** Width of the selection checkbox column — the frozen name column starts after it. */
const SELECT_COL_W = 48;
/** thead's own `action.hover` wash, as a value the frozen header cells can re-paint. */
const HEAD_TINT = 'rgba(0,0,0,0.04)';

const stickyColSx = (left: number, tint?: string) => ({
  position: 'sticky' as const,
  left,
  zIndex: 2,
  bgcolor: 'background.paper',
  ...(tint ? { backgroundImage: `linear-gradient(${tint}, ${tint})` } : null),
});

function StatusChip({
  status,
  isActive,
  staged,
  detail,
}: {
  status: Presence;
  isActive?: boolean;
  staged?: boolean;
  detail?: React.ReactNode;
}) {
  const label = staged ? 'Staged' : status;
  const tone = staged
    ? { bg: '#f3e8ff', fg: '#9333ea', border: '#e9d5ff' }
    : status === 'Present'
      ? { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0' }
      : status === 'Modified'
        ? { bg: '#fef3c7', fg: '#b45309', border: '#fde68a' }
        : { bg: '#f8fafc', fg: '#64748b', border: '#e2e8f0' };

  const chip = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 24,
        pl: 0.75,
        pr: 1,
        borderRadius: 1,
        bgcolor: tone.bg,
        color: tone.fg,
        border: '1px solid',
        borderColor: tone.border,
        fontSize: 10,
        fontWeight: 800,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: status === 'Not present' && !staged ? '#cbd5e1' : staged || isActive ? '#f0fdf4' : '#94a3b8',
          boxShadow: staged || isActive ? '0 0 4px rgba(34,197,94,0.6)' : 'none',
        }}
      />
      {label}
      {detail ? <InfoOutlinedIcon sx={{ fontSize: 11, opacity: 0.6 }} /> : null}
    </Box>
  );

  return detail ? (
    <Tooltip title={detail} placement="top" arrow>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
}

function SummaryBar({ icon, total, attention, attentionLabel }: { icon: React.ReactNode; total: string; attention: number; attentionLabel: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.5,
        mb: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      {icon}
      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{total}</Typography>
      <Divider orientation="vertical" flexItem />
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: attention > 0 ? 'warning.dark' : 'text.disabled' }}>
        {attention} {attentionLabel}
      </Typography>
    </Box>
  );
}

function FilterBar({
  placeholder,
  query,
  onQuery,
  onlyDifferences,
  onOnlyDifferences,
}: {
  placeholder: string;
  query: string;
  onQuery: (v: string) => void;
  onlyDifferences: boolean;
  onOnlyDifferences: (v: boolean) => void;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
      <Box sx={{ minWidth: 280, flex: '0 1 320px' }}>
        <input className={studioStyles.input} placeholder={placeholder} value={query} onChange={(e) => onQuery(e.target.value)} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2, borderLeft: '1px solid', borderColor: 'divider' }}>
        <Switch size="small" checked={onlyDifferences} onChange={(e) => onOnlyDifferences(e.target.checked)} />
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Show only differences</Typography>
      </Box>
    </Box>
  );
}

/** Which experiment a new cluster goes under, by category: the canonical DCX experiment
 *  first, then any experiment whose name hints at the right kind, then whatever exists. */
const EXPERIMENT_RULES = {
  PHYSICAL: { exact: 'DCX_DEFAULT_CLUSTER', hints: ['physical', 'default'] },
  VIRTUAL: { exact: 'DCX_VIRTUAL_CLUSTERS', hints: ['virtual'] },
};

function resolveExperimentId(
  experiments: Array<{ id: string; name: string }>,
  clusterCategory: string | null
): string | undefined {
  if (experiments.length === 0) return undefined;
  const { exact, hints } = clusterCategory?.toUpperCase() === 'PHYSICAL' ? EXPERIMENT_RULES.PHYSICAL : EXPERIMENT_RULES.VIRTUAL;
  const named = experiments.find((e) => e.name === exact);
  const hinted = experiments.find((e) => hints.some((h) => e.name.toLowerCase().includes(h)));
  return (named ?? hinted ?? experiments[0]).id;
}

/** Every experiment the compare resolved in one target env, from the clusters already there. */
function experimentsInTarget(rows: ClusterRow[], targetId: string): Array<{ id: string; name: string }> {
  const byId = new Map<string, string>();
  rows.forEach((r) =>
    r.targets.forEach((t) => {
      if (t.targetId === targetId && t.experimentId && !byId.has(t.experimentId)) {
        byId.set(t.experimentId, t.experimentName ?? '');
      }
    })
  );
  return Array.from(byId, ([id, name]) => ({ id, name }));
}

/** Cluster type pill — PHYSICAL / VIRTUAL. */
function TypeChip({ type }: { type: string | null }) {
  if (!type) return <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>—</Typography>;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: '#e2e8f0',
        color: '#1e3a5f',
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {type}
    </Box>
  );
}

/** Row action cluster shared by the Attributes and Cluster Inventory tabs. */
function TargetActionCell({
  status,
  staged,
  detail,
  isActive,
  directWrite,
  onUndo,
  onInitialize,
  onSync,
  extraActions,
}: {
  status: Presence;
  staged?: boolean;
  detail?: React.ReactNode;
  isActive?: boolean;
  /** true for uat/dev/productqa targets (writes straight through); false for nonprod/prod (stages). */
  directWrite?: boolean;
  onUndo: () => void | Promise<void>;
  onInitialize: () => void | Promise<void>;
  onSync: () => void | Promise<void>;
  extraActions?: React.ReactNode;
}) {
  /** Each cell owns its spinner — a stage/sync/unstage call only blocks its own row+target. */
  const [busy, setBusy] = useState(false);
  const run = async (action: () => void | Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <StatusChip status={status} isActive={isActive} staged={staged} detail={detail} />
      {staged ? (
        <Box
          component="button"
          type="button"
          disabled={busy}
          onClick={() => run(onUndo)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            border: 'none',
            bgcolor: 'transparent',
            cursor: busy ? 'default' : 'pointer',
            p: 0,
            fontSize: 10,
            fontWeight: 700,
            color: '#9333ea',
            '&:hover': { textDecoration: busy ? 'none' : 'underline' },
          }}
        >
          {busy && <CircularProgress size={10} sx={{ color: '#9333ea' }} />}
          Undo staging
        </Box>
      ) : status === 'Not present' ? (
        <StudioButton
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => run(onInitialize)}
          startIcon={busy ? <CircularProgress size={12} /> : <AddIcon sx={{ fontSize: 14 }} />}
        >
          {'Initialize'}
        </StudioButton>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {status === 'Modified' && (
            <Tooltip title={directWrite ? 'Sync source value directly to target' : 'Stage source value for promotion'}>
              <IconButton size="small" color="primary" disabled={busy} onClick={() => run(onSync)}>
                {busy ? <CircularProgress size={14} /> : <SyncIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          )}
          {extraActions}
        </Box>
      )}
    </Box>
  );
}

function AttributesTab({
  rows,
  isLoading,
  onlyDifferences,
  setOnlyDifferences,
  selected,
  setSelected,
  onStage,
  onUnstage,
  targets,
  showSelection,
  sourceEnvType,
}: {
  rows: AttributeRow[];
  isLoading: boolean;
  onlyDifferences: boolean;
  setOnlyDifferences: (v: boolean) => void;
  selected: Record<string, boolean>;
  setSelected: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onStage: (attributeId: string, value: string, targetId: string) => void | Promise<void>;
  onUnstage: (attributeId: string, targetId: string) => void | Promise<void>;
  targets: TargetInfo[];
  /** Selection only feeds the promotion bundle, which stage-only targets are the point of. */
  showSelection: boolean;
  sourceEnvType: string;
}) {
  const [query, setQuery] = useState('');

  const rowsWithDiff = useMemo(
    () => rows.map((a) => ({
      ...a,
      isDiff: a.targets.some((t) => t.staged || t.status !== 'Present'),
      /** Only a staged row is a real promotion candidate — selection (and the bundle it
       *  feeds) is restricted to these; everything else shows a disabled checkbox. */
      isStaged: a.targets.some((t) => t.staged),
    })),
    [rows]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rowsWithDiff
      .filter((a) => a.attributeId.toLowerCase().includes(q) && (!onlyDifferences || a.isDiff))
      .sort((a, b) => Number(!a.isDiff) - Number(!b.isDiff) || a.attributeId.localeCompare(b.attributeId));
  }, [rowsWithDiff, query, onlyDifferences]);

  const stagedVisible = visible.filter((a) => a.isStaged);
  const allChecked = stagedVisible.length > 0 && stagedVisible.every((a) => selected[a.attributeId] === true);
  const colCount = (showSelection ? 4 : 3) + targets.length;

  return (
    <Box>
      <SummaryBar
        icon={<FingerprintIcon color="primary" sx={{ fontSize: 18 }} />}
        total={`${rows.length} user attributes compared`}
        attention={rowsWithDiff.filter((a) => a.isDiff).length}
        attentionLabel="discrepancies detected"
      />
      <FilterBar
        placeholder="Filter attributes…"
        query={query}
        onQuery={setQuery}
        onlyDifferences={onlyDifferences}
        onOnlyDifferences={setOnlyDifferences}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead" sx={{ bgcolor: 'action.hover' }}>
              <Box component="tr">
                {showSelection && (
                  <Box component="th" sx={{ ...headSx, width: 48, textAlign: 'center' }}>
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      disabled={stagedVisible.length === 0}
                      onChange={(e) =>
                        setSelected((prev) => {
                          const next = { ...prev };
                          stagedVisible.forEach((a) => {
                            next[a.attributeId] = e.target.checked;
                          });
                          return next;
                        })
                      }
                    />
                  </Box>
                )}
                <Box component="th" sx={headSx}>Attribute Name</Box>
                <Box component="th" sx={headSx}>Evaluator Type ({sourceEnvType.toUpperCase()})</Box>
                <Box component="th" sx={headSx}>Attribute Value ({sourceEnvType.toUpperCase()})</Box>
                {targets.map((t) => (
                  <Box component="th" key={t.id} sx={headSx}>{t.label}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {visible.map((attr) => (
                <Box
                  component="tr"
                  key={attr.attributeId}
                  sx={rowStateSx(
                    attr.targets.some((t) => t.staged),
                    attr.isDiff && !attr.targets.some((t) => t.staged)
                  )}
                >
                  {showSelection && (
                    <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
                      <Checkbox
                        size="small"
                        checked={attr.isStaged && selected[attr.attributeId] === true}
                        disabled={!attr.isStaged}
                        onChange={(e) => setSelected((prev) => ({ ...prev, [attr.attributeId]: e.target.checked }))}
                      />
                    </Box>
                  )}
                  <Box component="td" sx={{ ...cellSx, fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>{attr.attributeId}</Box>
                  <Box component="td" sx={cellSx}>
                    <Typography sx={{ fontSize: 12 }}>{attr.userAttributeEvaluatorType ?? '—'}</Typography>
                  </Box>
                  <Box component="td" sx={cellSx}>
                    <Typography sx={{ fontSize: 12, fontFamily: 'monospace' }}>{attr.sourceValue ?? '—'}</Typography>
                  </Box>
                  {targets.map((t) => {
                    const tr = attr.targets.find((x) => x.targetId === t.id);
                    const status = tr?.status ?? 'Not present';
                    return (
                      <Box component="td" key={t.id} sx={{ ...cellSx, minWidth: 240 }}>
                        <TargetActionCell
                          status={status}
                          staged={tr?.staged}
                          directWrite={t.directWrite}
                          detail={status === 'Modified' ? `Source: ${attr.sourceValue} → ${tr?.value}` : undefined}
                          onUndo={() => onUnstage(attr.attributeId, t.id)}
                          onInitialize={() => onStage(attr.attributeId, attr.sourceValue ?? '', t.id)}
                          onSync={() => onStage(attr.attributeId, attr.sourceValue ?? '', t.id)}
                          extraActions={
                            <Tooltip
                              title={
                                <>
                                  <div>userAttributeEvaluatorType: {tr?.userAttributeEvaluatorType ?? '—'}</div>
                                  <div>attributeValue: {tr?.stagedValue ?? tr?.value ?? '—'}</div>
                                </>
                              }
                            >
                              <IconButton size="small" color="primary">
                                <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          }
                        />
                      </Box>
                    );
                  })}
                </Box>
              ))}
              {visible.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={colCount} sx={{ ...cellSx, textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    No attributes match the current filters.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ClusterInventoryTab({
  rows,
  isLoading,
  onlyDifferences,
  setOnlyDifferences,
  selected,
  setSelected,
  onStage,
  onUnstage,
  targets,
  showSelection,
  sourceEnvType,
}: {
  rows: ClusterRow[];
  isLoading: boolean;
  onlyDifferences: boolean;
  setOnlyDifferences: (v: boolean) => void;
  selected: Record<string, boolean>;
  setSelected: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onStage: (clusterReference: string, evaluationCriteria: string, targetId: string) => void | Promise<void>;
  onUnstage: (clusterReference: string, targetId: string) => void | Promise<void>;
  targets: TargetInfo[];
  showSelection: boolean;
  sourceEnvType: string;
}) {
  const [query, setQuery] = useState('');

  const rowsWithDiff = useMemo(
    () => rows.map((c) => ({
      ...c,
      isDiff: c.targets.some((t) => t.staged || t.status !== 'Present'),
      /** Only a staged row is a real promotion candidate — selection (and the bundle it
       *  feeds) is restricted to these; everything else shows a disabled checkbox. */
      isStaged: c.targets.some((t) => t.staged),
    })),
    [rows]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rowsWithDiff
      .filter((c) => c.clusterName.toLowerCase().includes(q) && (!onlyDifferences || c.isDiff))
      .sort((a, b) => Number(!a.isDiff) - Number(!b.isDiff) || a.clusterName.localeCompare(b.clusterName));
  }, [rowsWithDiff, query, onlyDifferences]);

  const stagedVisible = visible.filter((c) => c.isStaged);
  const allChecked = stagedVisible.length > 0 && stagedVisible.every((c) => selected[c.clusterReference] === true);
  const colCount = (showSelection ? 4 : 3) + targets.length;

  return (
    <Box>
      <SummaryBar
        icon={<LayersIcon color="primary" sx={{ fontSize: 18 }} />}
        total={`${rows.length} clusters compared`}
        attention={rowsWithDiff.filter((c) => c.isDiff).length}
        attentionLabel="need attention"
      />
      <FilterBar
        placeholder="Search by reference name…"
        query={query}
        onQuery={setQuery}
        onlyDifferences={onlyDifferences}
        onOnlyDifferences={setOnlyDifferences}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead" sx={{ bgcolor: 'action.hover' }}>
              <Box component="tr">
                {showSelection && (
                  <Box component="th" sx={{ ...headSx, width: 48, textAlign: 'center' }}>
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      disabled={stagedVisible.length === 0}
                      onChange={(e) =>
                        setSelected((prev) => {
                          const next = { ...prev };
                          stagedVisible.forEach((c) => {
                            next[c.clusterReference] = e.target.checked;
                          });
                          return next;
                        })
                      }
                    />
                  </Box>
                )}
                <Box component="th" sx={headSx}>Reference name</Box>
                <Box component="th" sx={headSx}>Type</Box>
                <Box component="th" sx={headSx}>Evaluation Criteria ({sourceEnvType.toUpperCase()})</Box>
                {targets.map((t) => (
                  <Box component="th" key={t.id} sx={headSx}>{t.label}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {visible.map((cluster) => (
                <Box
                  component="tr"
                  key={cluster.clusterReference}
                  sx={rowStateSx(
                    cluster.targets.some((t) => t.staged),
                    cluster.isDiff && !cluster.targets.some((t) => t.staged)
                  )}
                >
                  {showSelection && (
                    <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
                      <Checkbox
                        size="small"
                        checked={cluster.isStaged && selected[cluster.clusterReference] === true}
                        disabled={!cluster.isStaged}
                        onChange={(e) => setSelected((prev) => ({ ...prev, [cluster.clusterReference]: e.target.checked }))}
                      />
                    </Box>
                  )}
                  <Box component="td" sx={{ ...cellSx, fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>
                    {/* Description (else the cluster's name) shows on hover only. */}
                    <Tooltip title={cluster.description ?? cluster.clusterName ?? 'No description'} placement="top" arrow>
                      <Box component="span">{cluster.clusterReference}</Box>
                    </Tooltip>
                  </Box>
                  <Box component="td" sx={cellSx}>
                    <TypeChip type={cluster.clusterCategory} />
                  </Box>
                  <Box component="td" sx={cellSx}>
                    <Typography sx={{ fontSize: 11, fontFamily: 'monospace' }}>{cluster.sourceValue ?? '—'}</Typography>
                  </Box>
                  {targets.map((t) => {
                    const tr = cluster.targets.find((x) => x.targetId === t.id);
                    const status = tr?.status ?? 'Not present';
                    return (
                      <Box component="td" key={t.id} sx={{ ...cellSx, minWidth: 260 }}>
                        <TargetActionCell
                          status={status}
                          staged={tr?.staged}
                          directWrite={t.directWrite && !!tr?.clusterId}
                          detail={status === 'Modified' ? `Source: ${cluster.sourceValue} → ${tr?.value}` : undefined}
                          onUndo={() => onUnstage(cluster.clusterReference, t.id)}
                          onInitialize={() => onStage(cluster.clusterReference, cluster.sourceValue ?? '', t.id)}
                          onSync={() => onStage(cluster.clusterReference, cluster.sourceValue ?? '', t.id)}
                        />
                      </Box>
                    );
                  })}
                </Box>
              ))}
              {visible.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={colCount} sx={{ ...cellSx, textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    No clusters match the current filters.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** Read-only — DCX Matrix (feature-metric "sheet") compare has no staging table, per MD. */
/** Same channel→icon vocabulary as the CX Editor's TemplatesSidebar (components/wysiwyg). */
const PRODUCT_ICONS: Record<string, React.ElementType> = {
  EMAIL: Mail,
  HER: Newspaper,
  DASHBOARD: Globe,
  WEB: Globe,
  SMS: MessageSquare,
};
function productIconFor(product: string) {
  const Icon = PRODUCT_ICONS[product.toUpperCase()];
  return Icon ? <Icon size={16} /> : null;
}

/** The four products the sheet compare covers — matrix opens scoped to EMAIL. */
const PRODUCTS = ['EMAIL', 'DASHBOARD', 'HER', 'SMS'];
const PRODUCT_LABELS: Record<string, string> = { EMAIL: 'Email', DASHBOARD: 'Dashboard', HER: 'HER', SMS: 'SMS' };

const FUEL_TYPES = ['ELECTRIC', 'GAS', 'WATER'];
const FUEL_TYPE_LABELS: Record<string, string> = { ELECTRIC: 'Electric', GAS: 'Gas', WATER: 'Water' };

/** Fuel type sits on the feature-metric object directly on some responses and under
 *  `metadata` on others — check both. */
const fuelTypeOf = (o?: Record<string, any> | null): string | null => o?.fuelType ?? o?.metadata?.fuelType ?? null;

/** Label comes straight from the API's displayName — never derived from the dotted id. */
const displayNameOf = (o?: Record<string, any> | null): string | null => o?.displayName ?? null;

/** Which dashboard page a feature-metric row belongs to — same `metadata.mappedPage` field
 *  TemplatePersonalizationPage reads off this API. */
const mappedPageOf = (o?: Record<string, any> | null): string | null =>
  o?.metadata?.mappedPage ?? o?.metadata?.mapped_page ?? o?.mappedPage ?? null;

/** Segment id for the source env, which has no entry in the resolved `targets` list. */
const SOURCE_ENV_ID = '__source';

const LEVEL_1 = 'LEVEL_1';
const LEVEL_2 = 'LEVEL_2';

/** Segmented control labels, per the Studio matrix design. */
const LEVEL_LABELS: Record<string, string> = {
  [LEVEL_1]: 'L1 Feature Access',
  [LEVEL_2]: 'L2 Template Composition',
};

/**
 * Which template/product a row belongs to. EMAIL fans out into one entry per L2 template group
 * (product.subproduct.template); DASHBOARD/HER/SMS stay a single entry each — the same
 * collapsing TemplatePersonalizationPage's product dropdown does.
 */
const applicationBaseOf = (row: SheetRow): string => {
  const product = row.product.toUpperCase();
  return product === 'EMAIL' ? row.featureId.split('.').slice(0, 3).join('.') : product;
};

/** Dropdown entries are per template *and* fuel type — a template's electric and gas modules
 *  are separate configurations, so they get separate entries. */
const applicationKeyOf = (row: SheetRow): string =>
  row.fuelType ? `${applicationBaseOf(row)}::${row.fuelType}` : applicationBaseOf(row);

/** Cell tones from the Studio matrix design: check on green, dash on amber, X on red. */
const RESOLUTION_TONES: Record<string, { bg: string; border: string; fg: string }> = {
  access: { bg: '#dcfce7', border: '#bbf7d0', fg: '#15803d' },
  pass: { bg: '#fef3c7', border: '#fde68a', fg: '#b45309' },
  kill: { bg: '#fee2e2', border: '#fecaca', fg: '#b91c1c' },
};
const RESOLUTION_NOT_SET = { bg: '#f8fafc', border: '#cbd5e1', fg: '#94a3b8' };

/**
 * One column segment (source, or one per resolved target) inside a feature×cluster cell.
 * A segment for an env the feature/cluster doesn't exist in is a flat slate block, not a
 * resolution — same as the design.
 */
function ResolutionSegment({
  label,
  resolution,
  present = true,
}: {
  label: string;
  resolution: string | null;
  present?: boolean;
}) {
  if (!present) {
    return (
      <Tooltip title={`${label} · Not present`}>
        <Box sx={{ flex: 1, height: 24, bgcolor: 'rgba(226,232,240,0.5)' }} />
      </Tooltip>
    );
  }

  const key = resolution?.toLowerCase() ?? '';
  const tone = RESOLUTION_TONES[key] ?? RESOLUTION_NOT_SET;
  return (
    <Tooltip title={`${label} · ${resolution ?? 'Not set'}`}>
      <Box
        sx={{
          flex: 1,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: tone.bg,
          border: '1px solid',
          borderColor: tone.border,
          borderStyle: key in RESOLUTION_TONES ? 'solid' : 'dashed',
        }}
      >
        {key === 'access' && <CheckIconLucide size={12} color={tone.fg} />}
        {key === 'kill' && <XIconLucide size={12} color={tone.fg} />}
        {key === 'pass' && <Box sx={{ width: 8, height: 2, bgcolor: tone.fg }} />}
      </Box>
    </Tooltip>
  );
}

/** Wrapper for the per-env segment strips (resolution, position, mandatory) in a matrix cell. */
function SegmentStrip({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '1px',
        maxWidth: 100,
        mx: 'auto',
        borderRadius: 0.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: '#dedede',
        boxShadow: 1,
      }}
    >
      {children}
    </Box>
  );
}

/** Module-level property (position / mandatory) for one env, alongside the resolution strips. */
function PropSegment({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip title={label}>
      <Box
        sx={{
          flex: 1,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(241,245,249,0.5)',
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

/** Green when the feature/cluster exists in that env, faded when it doesn't. */
function PresenceTag({ label, present }: { label: string; present: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        px: 0.5,
        borderRadius: 0.5,
        fontSize: 8,
        fontWeight: 900,
        lineHeight: '12px',
        bgcolor: present ? '#22c55e' : '#e2e8f0',
        color: present ? '#fff' : '#94a3b8',
        opacity: present ? 1 : 0.5,
      }}
    >
      {label}
    </Box>
  );
}

type SheetGridRow = { featureId: string; displayName: string | null; mappedPage: string | null; sourcePosition: number | null; cells: Map<string, SheetRow>; isDiff: boolean; isStaged: boolean };

/** Core name of a DASHBOARD featureId — "DASHBOARD" prefix and any trailing fuel type
 *  stripped, e.g. "DASHBOARD.ENERGY_INSIGHTS.ELECTRIC" -> "ENERGY_INSIGHTS". A row whose core
 *  name equals its own mappedPage IS that page; everything else is a feature under one. */
const dashboardCoreName = (featureId: string): string => {
  const parts = featureId.split('.').slice(1);
  const last = parts[parts.length - 1];
  return FUEL_TYPES.includes(last) ? parts.slice(0, -1).join('.') : parts.join('.');
};

/** Row label — the API's displayName unless it's merely the row's fuel (see utils/feature-id). */
const sheetRowLabel = (row: { displayName: string | null; featureId: string }): string =>
  resolveFeatureDisplayName(row.displayName, row.featureId);

const formatPageName = titleCaseSegment;

/**
 * Key for looking a row up in an environment's staged set.
 *
 * Staged rows are keyed by the featureId as staged AT THE TARGET, but a compare row carries the
 * SOURCE's id — and those differ whenever the mandatory flag differs, because the flag is a
 * segment of the id (`…TEASER.TRUE.ELECTRIC` vs `…TEASER.FALSE.ELECTRIC`). Comparing the raw
 * strings misses exactly the rows that differ, so the flag is blanked on both sides. Same rule
 * the backend's `feature_id_without_mandatory` uses to pair rows for the diff itself.
 */
const stagedKeyOf = (featureId: string, clusterReference: string): string => {
  const normalized = featureId
    .split('.')
    .map((part) => (part.toUpperCase() === 'TRUE' || part.toUpperCase() === 'FALSE' ? '*' : part))
    .join('.');
  return `${normalized}::${clusterReference}`;
};

/** A grouped row list: banner rows interleaved with the matrix rows they head. */
type SheetDisplayItem =
  /** `nested` marks a heading that sits inside another block (a page's "Usage" run), so it
   *  reads as a sub-heading rather than as another page. */
  | { kind: 'group'; key: string; label: string; nested?: boolean }
  /** `label` overrides the row's own name — set when a group heading already carries the part
   *  every row beneath it shares, so the row shows only what differs. */
  | { kind: 'row'; row: SheetGridRow; label?: string };

/**
 * Read-only against the compare response itself — a cell isn't clickable — but sheets do have
 * a staging table (`da_dcx_sheet_staging`, populated by Import Events), so a row is "staged"
 * when any of its (featureId, clusterReference) cells is in the staged set for any target.
 * Checkboxes only control what lands in the downloaded diff CSV, same PR flow as
 * Attributes/Clusters (shared footer), not a real write against any API — but, matching those
 * two tabs, only staged rows are real promotion candidates so selection is restricted to them.
 */
function SheetsTab({
  rows,
  isLoading,
  onlyDifferences,
  setOnlyDifferences,
  targets,
  sourceEnvLabel,
  sourceEnvType,
  showSelection,
  selected,
  setSelected,
  stagedByTarget,
}: {
  rows: SheetRow[];
  isLoading: boolean;
  onlyDifferences: boolean;
  setOnlyDifferences: (v: boolean) => void;
  targets: TargetInfo[];
  sourceEnvLabel: string;
  sourceEnvType: string;
  showSelection: boolean;
  selected: Record<string, boolean>;
  setSelected: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  /** Target env id -> `${featureId}::${clusterReference}` -> staged resolution value. A staged
   *  value can be "" or null, so membership is tested with `key in …`, not truthiness. */
  stagedByTarget: Record<string, Record<string, string | null>>;
}) {
  /** Opens on L1 feature access, scoped to EMAIL; L2 additionally narrows to one application. */
  const [level, setLevel] = useState(LEVEL_1);
  const [productFilter, setProductFilter] = useState('EMAIL');
  const [fuelFilter, setFuelFilter] = useState('ELECTRIC');
  const [product, setProduct] = useState('');
  const isL2 = level === LEVEL_2;

  const levels = useMemo(() => Array.from(new Set(rows.map((r) => r.level))).sort(), [rows]);
  const levelRows = useMemo(() => rows.filter((r) => r.level === level), [rows, level]);
  const productLevelRows = useMemo(
    () => levelRows.filter((r) => r.product.toUpperCase() === productFilter),
    [levelRows, productFilter]
  );
  const scopedRows = useMemo(
    () => (fuelFilter === 'ALL' ? productLevelRows : productLevelRows.filter((r) => (r.fuelType ?? '').toUpperCase() === fuelFilter)),
    [productLevelRows, fuelFilter]
  );

  /** L2 module rows don't carry their template's own name — it lives on the matching LEVEL_1
   *  row, so the dropdown labels come from there. Whole products (DASHBOARD/HER/SMS) have no
   *  such row and keep their product token. */
  const applicationNames = useMemo(() => {
    const byBase = new Map<string, string>();
    rows.forEach((r) => {
      const base = applicationBaseOf(r);
      if (r.level === LEVEL_1 && r.displayName && base.includes('.')) byBase.set(base, r.displayName);
    });
    return byBase;
  }, [rows]);

  /** L2 only — one entry per template group, scoped to the selected product and fuel type. */
  const applications = useMemo(() => {
    if (!isL2) return [];
    const byKey = new Map<string, { key: string; label: string; product: string }>();
    scopedRows.forEach((r) => {
      const key = applicationKeyOf(r);
      if (byKey.has(key)) return;
      const base = applicationBaseOf(r);
      byKey.set(key, {
        key,
        label: withFuelLabel(applicationNames.get(base) ?? base, r.fuelType),
        product: r.product.toUpperCase(),
      });
    });
    return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [scopedRows, isL2, applicationNames]);

  /** Fall back to whatever the response does have if it carries no LEVEL_1 rows. */
  useEffect(() => {
    if (levels.length && !levels.includes(level)) setLevel(levels[0]);
  }, [levels, level]);

  /** Keep the picked application, or land on the first one for the current level. */
  useEffect(() => {
    if (applications.length) {
      setProduct((prev) => (applications.some((a) => a.key === prev) ? prev : applications[0].key));
    }
  }, [applications]);

  const filteredRows = useMemo(
    () => (isL2 && product ? scopedRows.filter((r) => applicationKeyOf(r) === product) : scopedRows),
    [scopedRows, isL2, product]
  );

  const clusterColumns = useMemo(
    () => Array.from(new Set(filteredRows.map((r) => r.clusterReference))).sort(),
    [filteredRows]
  );

  const stagedTargetMaps = useMemo(() => Object.values(stagedByTarget), [stagedByTarget]);

  const gridRows = useMemo(() => {
    const byFeature = new Map<string, SheetGridRow>();
    filteredRows.forEach((r) => {
      let entry = byFeature.get(r.featureId);
      if (!entry) {
        entry = { featureId: r.featureId, displayName: r.displayName, mappedPage: r.mappedPage, sourcePosition: r.sourcePosition, cells: new Map(), isDiff: false, isStaged: false };
        byFeature.set(r.featureId, entry);
      }
      entry.cells.set(r.clusterReference, r);
      if (r.targets.some((t) => t.status !== 'Present')) entry.isDiff = true;
      const key = stagedKeyOf(r.featureId, r.clusterReference);
      if (stagedTargetMaps.some((staged) => key in staged)) entry.isStaged = true;
    });
    return Array.from(byFeature.values());
  }, [filteredRows, stagedTargetMaps]);

  /** Rows follow the source env's module order; anything without a position sorts last. */
  const visibleRows = useMemo(
    () =>
      gridRows
        .filter((r) => !onlyDifferences || r.isDiff)
        .sort(
          (a, b) =>
            (a.sourcePosition ?? Number.MAX_SAFE_INTEGER) - (b.sourcePosition ?? Number.MAX_SAFE_INTEGER) ||
            a.featureId.localeCompare(b.featureId)
        ),
    [gridRows, onlyDifferences]
  );

  const visibleClusters = useMemo(() => {
    if (!onlyDifferences) return clusterColumns;
    return clusterColumns.filter((c) =>
      visibleRows.some((r) => {
        const cell = r.cells.get(c);
        return cell && cell.targets.some((t) => t.status !== 'Present');
      })
    );
  }, [clusterColumns, onlyDifferences, visibleRows]);

  /**
   * DASHBOARD reads as pages: a "Pages" block listing the page rows themselves, then one block
   * per page holding its features, then "Other" for features whose page isn't in this view —
   * the same arrangement TemplatePersonalizationPage's dashboard grid uses. Rows arrive already
   * sorted by source position, so each block keeps that order. Any other product (or dashboard
   * data with no page rows at all, e.g. L1) stays a flat list.
   */
  const displayItems = useMemo<SheetDisplayItem[]>(() => {
    const asRows = (rows: SheetGridRow[]): SheetDisplayItem[] => rows.map((row) => ({ kind: 'row', row }));
    if (productFilter !== 'DASHBOARD') return asRows(visibleRows);

    const pageRows = visibleRows.filter((r) => !!r.mappedPage && dashboardCoreName(r.featureId) === r.mappedPage);
    if (pageRows.length === 0) return asRows(visibleRows);

    const featureRows = visibleRows.filter((r) => !pageRows.includes(r));
    const items: SheetDisplayItem[] = [{ kind: 'group', key: '__pages__', label: 'Pages' }];
    pageRows.forEach((row) => items.push({ kind: 'row', row }));

    /**
     * A page's rows, with its usage-mode ones gathered under a nested "Usage" heading — they
     * all repeat the word in their label, so the heading carries it and each row shows only
     * what differs ("Appliance View · Day"). Stays INSIDE the page; a usage row belongs to its
     * page, not to a block of its own.
     */
    const pushPageRows = (rows: SheetGridRow[], keyPrefix: string) => {
      const usage = rows.filter((r) => dashboardUsageDetailParts(r.featureId));
      rows.filter((r) => !dashboardUsageDetailParts(r.featureId)).forEach((row) => items.push({ kind: 'row', row }));
      if (usage.length === 0) return;
      items.push({ kind: 'group', key: `${keyPrefix}-usage`, label: 'Usage', nested: true });
      usage.forEach((row) =>
        items.push({ kind: 'row', row, label: dashboardUsageDetailParts(row.featureId)!.join(' · ') })
      );
    };

    const pageNames = new Set(pageRows.map((r) => r.mappedPage as string));
    pageRows.forEach((pageRow) => {
      const pageName = pageRow.mappedPage as string;
      const features = featureRows.filter((f) => f.mappedPage === pageName);
      if (features.length === 0) return;
      items.push({ kind: 'group', key: `page-${pageName}`, label: formatPageName(pageName) });
      pushPageRows(features, `page-${pageName}`);
    });

    const orphans = featureRows.filter((f) => !f.mappedPage || !pageNames.has(f.mappedPage));
    if (orphans.length > 0) {
      items.push({ kind: 'group', key: '__other__', label: 'Other' });
      pushPageRows(orphans, '__other__');
    }

    return items;
  }, [visibleRows, productFilter]);

  const stagedVisibleRows = useMemo(() => visibleRows.filter((r) => r.isStaged), [visibleRows]);
  const allChecked = stagedVisibleRows.length > 0 && stagedVisibleRows.every((r) => selected[r.featureId] === true);

  /** Module-level properties, L2 only — L1 feature rows carry neither. Mandatory is an
   *  email-template notion; dashboard/HER modules don't have the flag at all. */
  const showMandatory = isL2 && productFilter === 'EMAIL';
  const propColumns = [...(isL2 ? ['Pos'] : []), ...(showMandatory ? ['Mandatory'] : [])];

  /** Source env first, then each resolved target — the segment order in every strip.
   *  `label` names the env in tooltips; `tag` is the env type shown on the compact chips. */
  const envColumns = useMemo(
    () => [
      { id: SOURCE_ENV_ID, label: sourceEnvLabel.toUpperCase(), tag: (sourceEnvType || sourceEnvLabel).toUpperCase() },
      ...targets.map((t) => ({ id: t.id, label: t.label, tag: t.envType })),
    ],
    [sourceEnvLabel, sourceEnvType, targets]
  );

  /** A feature/cluster counts as present in a target env when any of its cells isn't NOT_PRESENT. */
  const rowPresence = (row: SheetGridRow, envId: string) =>
    envId === SOURCE_ENV_ID ||
    Array.from(row.cells.values()).some((c) => {
      const tr = c.targets.find((t) => t.targetId === envId);
      return !!tr && tr.status !== 'Not present';
    });

  const clusterPresence = (cluster: string, envId: string) =>
    envId === SOURCE_ENV_ID ||
    visibleRows.some((r) => {
      const tr = r.cells.get(cluster)?.targets.find((t) => t.targetId === envId);
      return !!tr && tr.status !== 'Not present';
    });

  return (
    <Box>
      <SummaryBar
        icon={<LayersIcon color="primary" sx={{ fontSize: 18 }} />}
        total={`${gridRows.length} features compared`}
        attention={gridRows.filter((r) => r.isDiff).length}
        attentionLabel="need attention"
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'action.hover', boxShadow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: '#f1f5f9' }}>
            {levels.map((l) => (
              <Box
                key={l}
                component="button"
                type="button"
                onClick={() => setLevel(l)}
                sx={{
                  height: 28,
                  px: 2,
                  border: 'none',
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  color: level === l ? 'text.primary' : 'text.secondary',
                  bgcolor: level === l ? '#e2e8f0' : 'transparent',
                  '&:hover': { bgcolor: level === l ? '#e2e8f0' : 'action.hover' },
                }}
              >
                {LEVEL_LABELS[l] ?? l.replace('_', ' ')}
              </Box>
            ))}
          </Box>
          {/* Product narrows the matrix to one of the four DCX channels — opens on EMAIL. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.secondary' }}>Product:</Typography>
            <Select
              size="small"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              renderValue={(val) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {productIconFor(String(val))}
                  <span>{PRODUCT_LABELS[String(val)] ?? String(val)}</span>
                </Box>
              )}
              sx={{
                height: 32,
                minWidth: 160,
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                fontSize: 12,
                fontWeight: 700,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: (t) => alpha(t.palette.primary.main, 0.2) },
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.75 },
              }}
            >
              {PRODUCTS.map((p) => (
                <MenuItem key={p} value={p} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 12, fontWeight: 700 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListItemIcon sx={{ minWidth: 0 }}>{productIconFor(p)}</ListItemIcon>
                    <span>{PRODUCT_LABELS[p] ?? p}</span>
                  </Box>
                  {productFilter === p && <CheckIconLucide size={16} />}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {/* Fuel narrows both L1 and L2 rows to one fuel type. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.secondary' }}>Fuel:</Typography>
            <Select
              size="small"
              value={fuelFilter}
              onChange={(e) => setFuelFilter(e.target.value)}
              sx={{
                height: 32,
                minWidth: 140,
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                fontSize: 12,
                fontWeight: 700,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: (t) => alpha(t.palette.primary.main, 0.2) },
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.75 },
              }}
            >
              <MenuItem value="ALL" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 12, fontWeight: 700 }}>
                <span>All Fuel Types</span>
                {fuelFilter === 'ALL' && <CheckIconLucide size={16} />}
              </MenuItem>
              {FUEL_TYPES.map((f) => (
                <MenuItem key={f} value={f} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 12, fontWeight: 700 }}>
                  <span>{FUEL_TYPE_LABELS[f] ?? f}</span>
                  {fuelFilter === f && <CheckIconLucide size={16} />}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {/* Application narrows L2 modules to one template — meaningless for L1 rows. */}
          {isL2 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.secondary' }}>Application:</Typography>
              <Select
                size="small"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                displayEmpty
                renderValue={(val) => {
                  const picked = applications.find((a) => a.key === val);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {productIconFor(picked?.product ?? '')}
                      <span>{picked?.label ?? String(val)}</span>
                    </Box>
                  );
                }}
                sx={{
                  height: 32,
                  minWidth: 220,
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  fontSize: 12,
                  fontWeight: 700,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: (t) => alpha(t.palette.primary.main, 0.2) },
                  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.75 },
                }}
              >
                {applications.map((a) => (
                  <MenuItem key={a.key} value={a.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 12, fontWeight: 700 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ListItemIcon sx={{ minWidth: 0 }}>{productIconFor(a.product)}</ListItemIcon>
                      <span>{a.label}</span>
                    </Box>
                    {product === a.key && <CheckIconLucide size={16} />}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 1 }}>
          <Switch size="small" checked={onlyDifferences} onChange={(e) => setOnlyDifferences(e.target.checked)} />
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, fontWeight: 700 }}>
            <FilterIcon size={12} /> Differences only
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2, boxShadow: 2 }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead" sx={{ bgcolor: 'action.hover' }}>
              <Box component="tr" sx={{ height: 48 }}>
                {showSelection && (
                  <Box component="th" sx={{ ...headSx, ...stickyColSx(0, HEAD_TINT), zIndex: 3, width: SELECT_COL_W, textAlign: 'center', borderRight: '1px solid', borderRightColor: 'divider' }}>
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      disabled={stagedVisibleRows.length === 0}
                      onChange={(e) =>
                        setSelected((prev) => {
                          const next = { ...prev };
                          stagedVisibleRows.forEach((r) => {
                            next[r.featureId] = e.target.checked;
                          });
                          return next;
                        })
                      }
                    />
                  </Box>
                )}
                {/* Frozen: the row's identity has to stay readable while the clusters scroll. */}
                <Box component="th" sx={{ ...headSx, ...stickyColSx(showSelection ? SELECT_COL_W : 0, HEAD_TINT), zIndex: 3, width: 280, pl: 3, borderRight: '1px solid', borderRightColor: 'divider' }}>
                  {isL2 ? 'Module ID' : 'Feature ID'}
                </Box>
                {/* Position is a module property — L1 feature rows don't have one. */}
                {propColumns.map((title) => (
                  <Box component="th" key={title} sx={{ ...headSx, width: 96, textAlign: 'center', borderRight: '1px solid', borderRightColor: 'divider' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <span>{title}</span>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {envColumns.map((env) => (
                          <Box key={env.id} component="span" sx={{ px: 0.5, borderRadius: 0.5, fontSize: 7, fontWeight: 900, lineHeight: '12px', bgcolor: '#e2e8f0', color: 'text.secondary' }}>
                            {env.tag}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ))}
                {visibleClusters.map((cluster) => (
                  <Box component="th" key={cluster} sx={{ ...headSx, textAlign: 'center', minWidth: 120, px: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Box component="span" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cluster}</Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {envColumns.map((env) => (
                          <PresenceTag key={env.id} label={env.tag} present={clusterPresence(cluster, env.id)} />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {displayItems.map((item) => {
                if (item.kind === 'group') {
                  return (
                    <Box component="tr" key={item.key}>
                      <Box
                        component="td"
                        colSpan={(showSelection ? 1 : 0) + propColumns.length + 1 + visibleClusters.length}
                        sx={{
                          position: 'sticky',
                          left: 0,
                          px: 2,
                          py: item.nested ? 0.5 : 1,
                          pl: item.nested ? 5 : 2,
                          bgcolor: item.nested ? 'transparent' : 'action.hover',
                          borderTop: '1px solid',
                          borderBottom: item.nested ? '1px solid' : '2px solid',
                          borderTopColor: 'divider',
                          borderBottomColor: item.nested ? 'divider' : 'primary.main',
                          fontSize: item.nested ? 10 : 11,
                          fontWeight: item.nested ? 800 : 900,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          color: item.nested ? 'text.secondary' : 'text.primary',
                        }}
                      >
                        {item.label}
                      </Box>
                    </Box>
                  );
                }

                const row = item.row;
                /** position is module-level — every cluster cell of a row repeats it. */
                const propsCell: SheetRow | undefined = row.cells.values().next().value;
                const tint = row.isStaged
                  ? ROW_TINT.staged
                  : row.isDiff
                    ? ROW_TINT.attention
                    : undefined;
                return (
                  <Box
                    component="tr"
                    key={row.featureId}
                    sx={{ height: 56, ...rowStateSx(row.isStaged, row.isDiff && !row.isStaged) }}
                  >
                    {showSelection && (
                      <Box component="td" sx={{ ...cellSx, ...stickyColSx(0, tint), textAlign: 'center', borderRight: '1px solid', borderRightColor: 'divider' }}>
                        <Checkbox
                          size="small"
                          checked={row.isStaged && selected[row.featureId] === true}
                          disabled={!row.isStaged}
                          onChange={(e) => setSelected((prev) => ({ ...prev, [row.featureId]: e.target.checked }))}
                        />
                      </Box>
                    )}
                    <Box component="td" sx={{ ...cellSx, ...stickyColSx(showSelection ? SELECT_COL_W : 0, tint), pl: 3, borderRight: '1px solid', borderRightColor: 'divider' }}>
                      {/* Display name only — raw featureId stays in the tooltip. L1 names render in caps. */}
                      <Tooltip title={row.featureId} placement="top" arrow>
                        <Box component="span" sx={{ display: 'block', fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: isL2 ? 'none' : 'uppercase' }}>
                          {item.label ?? sheetRowLabel(row)}
                        </Box>
                      </Tooltip>
                    </Box>
                    {isL2 && (
                      <Box component="td" sx={{ ...cellSx, p: 0.5, borderRight: '1px solid', borderRightColor: 'divider' }}>
                        <SegmentStrip>
                          {envColumns.map((env) => {
                            const position = env.id === SOURCE_ENV_ID ? propsCell?.sourcePosition : propsCell?.targets.find((t) => t.targetId === env.id)?.position;
                            return (
                              <PropSegment key={env.id} label={`${env.label}: position ${position ?? 'not set'}`}>
                                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700 }}>{position ?? '—'}</Box>
                              </PropSegment>
                            );
                          })}
                        </SegmentStrip>
                      </Box>
                    )}
                    {showMandatory && (
                      <Box component="td" sx={{ ...cellSx, p: 0.5, borderRight: '1px solid', borderRightColor: 'divider' }}>
                        <SegmentStrip>
                          {envColumns.map((env) => {
                            const mandatory = env.id === SOURCE_ENV_ID
                              ? propsCell?.sourceMandatory
                              : propsCell?.targets.find((t) => t.targetId === env.id)?.mandatory;
                            return (
                              <PropSegment
                                key={env.id}
                                label={`${env.label}: ${mandatory == null ? 'mandatory not set' : mandatory ? 'mandatory' : 'optional'}`}
                              >
                                {mandatory == null ? (
                                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: '#94a3b8' }}>—</Box>
                                ) : mandatory ? (
                                  <CheckIconLucide size={10} color="#334155" />
                                ) : (
                                  <XIconLucide size={10} color="#cbd5e1" />
                                )}
                              </PropSegment>
                            );
                          })}
                        </SegmentStrip>
                      </Box>
                    )}
                    {visibleClusters.map((cluster) => {
                      const cell = row.cells.get(cluster);
                      if (!cell) {
                        return <Box component="td" key={cluster} sx={{ ...cellSx, p: 1 }}><Box sx={{ height: 24, maxWidth: 100, mx: 'auto', bgcolor: 'rgba(226,232,240,0.5)', borderRadius: 0.5 }} /></Box>;
                      }
                      return (
                        <Box component="td" key={cluster} sx={{ ...cellSx, p: 1 }}>
                          <SegmentStrip>
                            {envColumns.map((env) => {
                              const tr = env.id === SOURCE_ENV_ID ? null : cell.targets.find((t) => t.targetId === env.id);
                              const isSource = env.id === SOURCE_ENV_ID;
                              return (
                                <ResolutionSegment
                                  key={env.id}
                                  label={env.label}
                                  resolution={isSource ? cell.sourceResolution : tr?.resolution ?? null}
                                  present={isSource ? cell.sourceResolution != null : !!tr && tr.status !== 'Not present'}
                                />
                              );
                            })}
                          </SegmentStrip>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
              {visibleRows.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={(showSelection ? 1 : 0) + propColumns.length + 1 + visibleClusters.length} sx={{ ...cellSx, textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    No features match the current filters.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function ClusterDcxCompare({
  projectId,
  currentEnv,
  targetEnv,
  sourceEnvLabel,
  sourceEnvType,
  onBack,
}: {
  projectId: string;
  currentEnv: string;
  /** The single env id picked in the dropdown. If it's nonprod/prod, the backend
   *  automatically resolves and returns its pair too (see DCX_PROMOTION.md "Dual target"). */
  targetEnv: string;
  sourceEnvLabel: string;
  sourceEnvType: string;
  onBack: () => void;
}) {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const [tab, setTab] = useState<'attributes' | 'clusters' | 'sheets'>('attributes');
  const [onlyDifferences, setOnlyDifferences] = useState(false);

  /** Resolved from targetEnvironments on the compare responses — 1 or 2 entries. */
  const [targets, setTargets] = useState<TargetInfo[]>([]);

  const [attributeRows, setAttributeRows] = useState<AttributeRow[]>([]);
  const [clusterRows, setClusterRows] = useState<ClusterRow[]>([]);
  const [sheetRows, setSheetRows] = useState<SheetRow[]>([]);
  /** Target env id -> `${featureId}::${clusterReference}` -> staged resolution value (both
   *  levels) — drives the purple staged highlight/checkboxes on the DCX Matrix Audit tab, and
   *  is what actually goes into a sheet's PR bundle item (not today's live source value).
   *  Plain objects, not Map/Set: a staged resolution can legitimately be "" or null, so
   *  membership is tested with `key in …` rather than truthiness. */
  const [sheetStagedByTarget, setSheetStagedByTarget] = useState<Record<string, Record<string, string | null>>>({});
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);
  const [isLoadingClusters, setIsLoadingClusters] = useState(false);
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);

  const [attributeSelected, setAttributeSelected] = useState<Record<string, boolean>>({});
  const [clusterSelected, setClusterSelected] = useState<Record<string, boolean>>({});
  const [sheetSelected, setSheetSelected] = useState<Record<string, boolean>>({});

  const [prTargetId, setPrTargetId] = useState<string | null>(null);
  const [isBuildingBundle, setIsBuildingBundle] = useState(false);

  const loadAttributes = useCallback(async () => {
    setIsLoadingAttributes(true);
    const resp = await DCXPromotionApis.compareAttributes(rc, projectId, currentEnv, targetEnv);
    if (resp.success && resp.data) {
      setTargets(mapTargetEnvironments(resp.data.targetEnvironments));
      setAttributeRows(
        resp.data.data.map((row) => ({
          attributeId: row.attributeId,
          sourceValue: row.source?.attributeValue ?? null,
          userAttributeEvaluatorType: row.source?.userAttributeEvaluatorType ?? null,
          sourceRaw: row.source ?? null,
          targets: row.targets.map((t) => ({
            targetId: String(t.id),
            value: t.target?.attributeValue ?? null,
            userAttributeEvaluatorType: t.target?.userAttributeEvaluatorType ?? null,
            status: STATUS_TO_PRESENCE[t.status],
            staged: t.staged,
            stagedValue: t.stagedValue,
          })),
        }))
      );
    } else {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to load attribute comparison');
      setAttributeRows([]);
    }
    setIsLoadingAttributes(false);
  }, [rc, projectId, currentEnv, targetEnv, toast]);

  const loadClusters = useCallback(async () => {
    setIsLoadingClusters(true);
    const resp = await DCXPromotionApis.compareClusters(rc, projectId, currentEnv, targetEnv);
    if (resp.success && resp.data) {
      setTargets(mapTargetEnvironments(resp.data.targetEnvironments));
      setClusterRows(
        resp.data.data.map((row) => ({
          clusterReference: row.clusterReference,
          clusterName: row.source?.clusterName ?? row.clusterReference,
          description: row.source?.description ?? null,
          clusterCategory:
            row.source?.clusterCategory ?? row.targets.map((t) => t.target?.clusterCategory).find(Boolean) ?? null,
          sourceValue: row.source?.evaluationCriteria ?? null,
          targets: row.targets.map((t) => ({
            targetId: String(t.id),
            value: t.target?.evaluationCriteria ?? null,
            status: STATUS_TO_PRESENCE[t.status],
            staged: t.staged,
            stagedValue: t.stagedValue,
            clusterId: t.target?.clusterId ?? null,
            experimentId: t.target?.experimentId ?? null,
            experimentName: t.target?.experimentName ?? null,
          })),
        }))
      );
    } else {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to load cluster comparison');
      setClusterRows([]);
    }
    setIsLoadingClusters(false);
  }, [rc, projectId, currentEnv, targetEnv, toast]);

  /** One `getStagedSheets` call per target (both levels at once) — separate from the compare
   *  response itself, which carries no staged flags for sheets (unlike attributes/clusters). */
  const loadSheetStaged = useCallback(async (targetList: TargetInfo[]) => {
    if (targetList.length === 0) {
      setSheetStagedByTarget({});
      return;
    }
    const entries = await Promise.all(
      targetList.map(async (t) => {
        const resp = await DCXPromotionApis.getStagedSheets(rc, { target_env: t.id, project_id: projectId });
        const staged: Record<string, string | null> = {};
        if (resp.success && resp.data) {
          resp.data.data.forEach((row) => {
            staged[stagedKeyOf(row.featureId, row.clusterReference)] = row.resolution ?? null;
          });
        }
        return [t.id, staged] as const;
      })
    );
    setSheetStagedByTarget(Object.fromEntries(entries));
  }, [rc, projectId]);

  const loadSheets = useCallback(async () => {
    setIsLoadingSheets(true);
    const resp = await DCXPromotionApis.compareSheets(rc, projectId, currentEnv, targetEnv);
    if (resp.success && resp.data) {
      const resolvedTargets = mapTargetEnvironments(resp.data.targetEnvironments);
      setTargets(resolvedTargets);
      loadSheetStaged(resolvedTargets);
      setSheetRows(
        resp.data.data.map((row) => ({
          featureId: row.featureId,
          displayName: displayNameOf(row.source) ?? row.targets.map((t) => displayNameOf(t.target)).find(Boolean) ?? null,
          fuelType:
            fuelTypeOf(row.source) ??
            row.targets.map((t) => fuelTypeOf(t.target)).find(Boolean) ??
            // last resort: L2 ids end in the fuel type, e.g. …HEADER_V4.FALSE.ELECTRIC
            row.featureId.split('.').find((part) => FUEL_TYPES.includes(part)) ??
            null,
          clusterReference: row.clusterReference,
          product: row.product,
          level: row.level,
          mappedPage: mappedPageOf(row.source) ?? row.targets.map((t) => mappedPageOf(t.target)).find(Boolean) ?? null,
          sourceResolution: row.source?.resolution ?? null,
          sourcePosition: row.source?.position ?? null,
          sourceMandatory: row.source?.isMandatory ?? null,
          targets: row.targets.map((t) => ({
            targetId: String(t.id),
            resolution: t.target?.resolution ?? null,
            status: STATUS_TO_PRESENCE[t.status],
            position: t.target?.position ?? null,
            mandatory: t.target?.isMandatory ?? null,
          })),
        }))
      );
    } else {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to load feature sheet comparison');
      setSheetRows([]);
    }
    setIsLoadingSheets(false);
  }, [rc, projectId, currentEnv, targetEnv, toast, loadSheetStaged]);

  useEffect(() => {
    if (!projectId || !currentEnv || !targetEnv) return;
    loadAttributes();
    loadClusters();
    loadSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentEnv, targetEnv]);

  /** uat/dev/productqa targets: write straight through. nonprod/prod: stage for later PR. */
  const promoteAttribute = async (attributeId: string, value: string, targetId: string) => {
    const target = targets.find((t) => t.id === targetId);
    if (!target) return;

    if (target.directWrite) {
      const row = attributeRows.find((r) => r.attributeId === attributeId);
      const src = row?.sourceRaw ?? {};
      const isNotPresent = row?.targets.find((t) => t.targetId === targetId)?.status === 'Not present';

      const resp = isNotPresent
        ? await DCXPromotionApis.createAttribute(rc, {
            project_id: projectId,
            target_env: targetId,
            entityName: src.entityName ?? '',
            attributeName: src.attributeName ?? attributeId,
            attributeId,
            attributeValue: value,
            userAttributeEvaluatorType: src.userAttributeEvaluatorType ?? '',
            description: src.description ?? '',
          })
        : await DCXPromotionApis.updateAttribute(rc, {
            project_id: projectId,
            target_env: targetId,
            entityName: src.entityName ?? '',
            attributeName: src.attributeName ?? attributeId,
            attributeId,
            attributeValue: value,
            userAttributeEvaluatorType: src.userAttributeEvaluatorType ?? '',
            description: src.description ?? '',
          });

      if (resp.success) {
        setAttributeRows((prev) =>
          prev.map((r) =>
            r.attributeId !== attributeId
              ? r
              : { ...r, targets: r.targets.map((t) => (t.targetId === targetId ? { ...t, status: 'Present', value, staged: false, stagedValue: null } : t)) }
          )
        );
        toast.success(`${attributeId} synced to ${target.label}`);
      } else {
        toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to sync attribute');
      }
      return;
    }

    const resp = await DCXPromotionApis.stageAttribute(rc, {
      project_id: projectId,
      current_env: currentEnv,
      target_env: targetId,
      attributeId,
      attributeValue: value,
      sourceValue: value,
    });
    if (resp.success) {
      setAttributeRows((prev) =>
        prev.map((r) =>
          r.attributeId !== attributeId
            ? r
            : { ...r, targets: r.targets.map((t) => (t.targetId === targetId ? { ...t, staged: true, stagedValue: value } : t)) }
        )
      );
      setAttributeSelected((prev) => ({ ...prev, [attributeId]: true }));
    } else {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to stage attribute');
    }
  };

  const unstageAttribute = async (attributeId: string, targetId: string) => {
    const resp = await DCXPromotionApis.unstageAttribute(rc, targetId, projectId, attributeId);
    if (resp.success) {
      setAttributeRows((prev) =>
        prev.map((r) =>
          r.attributeId !== attributeId
            ? r
            : { ...r, targets: r.targets.map((t) => (t.targetId === targetId ? { ...t, staged: false, stagedValue: null } : t)) }
        )
      );
    } else {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to undo staging');
    }
  };

  /**
   * uat/dev/productqa targets: write straight through. nonprod/prod: stage for later PR.
   * A NOT_PRESENT row (no target clusterId yet) is created rather than updated; the target
   * env's experiment is resolved server-side from the cluster's category.
   */
  const promoteCluster = async (clusterReference: string, evaluationCriteria: string, targetId: string) => {
    const target = targets.find((t) => t.id === targetId);
    if (!target) return;
    const row = clusterRows.find((r) => r.clusterReference === clusterReference);
    const clusterId = row?.targets.find((t) => t.targetId === targetId)?.clusterId;

    if (target.directWrite && clusterId) {
      const resp = await DCXPromotionApis.updateCluster(rc, {
        project_id: projectId,
        target_env: targetId,
        clusterId,
        evaluationCriteria,
        clusterReference,
      });
      if (resp.success) {
        setClusterRows((prev) =>
          prev.map((r) =>
            r.clusterReference !== clusterReference
              ? r
              : { ...r, targets: r.targets.map((t) => (t.targetId === targetId ? { ...t, status: 'Present', value: evaluationCriteria, staged: false, stagedValue: null } : t)) }
          )
        );
        toast.success(`${clusterReference} synced to ${target.label}`);
      } else {
        toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to sync cluster');
      }
      return;
    }

    if (target.directWrite && !clusterId) {
      const resp = await DCXPromotionApis.createCluster(rc, {
        project_id: projectId,
        target_env: targetId,
        clusterReference,
        evaluationCriteria,
        clusterName: row?.clusterName ?? clusterReference,
        clusterCategory: row?.clusterCategory ?? undefined,
        // Undefined when the target env has no clusters yet — the BFF resolves it then.
        experimentId: resolveExperimentId(experimentsInTarget(clusterRows, targetId), row?.clusterCategory ?? null),
      });
      if (resp.success) {
        setClusterRows((prev) =>
          prev.map((r) =>
            r.clusterReference !== clusterReference
              ? r
              : {
                  ...r,
                  targets: r.targets.map((t) =>
                    t.targetId === targetId
                      ? { ...t, status: 'Present', value: evaluationCriteria, staged: false, stagedValue: null, clusterId: resp.data?.clusterId ?? t.clusterId }
                      : t
                  ),
                }
          )
        );
        toast.success(`${clusterReference} created in ${target.label}`);
      } else {
        toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to create cluster');
      }
      return;
    }

    const resp = await DCXPromotionApis.stageCluster(rc, {
      project_id: projectId,
      current_env: currentEnv,
      target_env: targetId,
      clusterReference,
      evaluationCriteria,
      sourceValue: evaluationCriteria,
    });
    if (resp.success) {
      setClusterRows((prev) =>
        prev.map((r) =>
          r.clusterReference !== clusterReference
            ? r
            : { ...r, targets: r.targets.map((t) => (t.targetId === targetId ? { ...t, staged: true, stagedValue: evaluationCriteria } : t)) }
        )
      );
      setClusterSelected((prev) => ({ ...prev, [clusterReference]: true }));
    } else {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to stage cluster');
    }
  };

  const unstageCluster = async (clusterReference: string, targetId: string) => {
    const resp = await DCXPromotionApis.unstageCluster(rc, targetId, projectId, clusterReference);
    if (resp.success) {
      setClusterRows((prev) =>
        prev.map((r) =>
          r.clusterReference !== clusterReference
            ? r
            : { ...r, targets: r.targets.map((t) => (t.targetId === targetId ? { ...t, staged: false, stagedValue: null } : t)) }
        )
      );
    } else {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to undo staging');
    }
  };

  /** uat/dev/productqa write straight through, so nothing ever stages for them — row selection
   *  and the PR bundle belong to the nonprod/prod targets alone. */
  const stageTargets = useMemo(() => targets.filter((t) => !t.directWrite), [targets]);

  /** Everything staged and still checked, split by target — one PR bundle per target env. */
  const bundleByTarget = useMemo(() => {
    const map = new Map<string, PromotionBundleItem[]>();
    targets.forEach((t) => map.set(t.id, []));

    attributeRows.forEach((row) => {
      if (attributeSelected[row.attributeId] !== true) return;
      row.targets.forEach((t) => {
        if (t.staged) map.get(t.targetId)?.push({ kind: 'attribute', row, target: t });
      });
    });

    clusterRows.forEach((row) => {
      if (clusterSelected[row.clusterReference] !== true) return;
      row.targets.forEach((t) => {
        if (t.staged) map.get(t.targetId)?.push({ kind: 'cluster', row, target: t });
      });
    });

    sheetRows.forEach((row) => {
      if (sheetSelected[row.featureId] !== true) return;
      const key = stagedKeyOf(row.featureId, row.clusterReference);
      row.targets.forEach((t) => {
        if (key in (sheetStagedByTarget[t.targetId] ?? {})) map.get(t.targetId)?.push({ kind: 'sheet', row, target: t });
      });
    });

    return map;
  }, [targets, attributeRows, clusterRows, sheetRows, attributeSelected, clusterSelected, sheetSelected, sheetStagedByTarget]);

  const prTarget = targets.find((t) => t.id === prTargetId) ?? null;
  const prItems = prTargetId ? bundleByTarget.get(prTargetId) ?? [] : [];

  /**
   * There's no backend "bulk-flush all staged rows" endpoint yet (per DCX_PROMOTION.md,
   * explicitly out of scope), so this generates a reviewable script instead: one curl per
   * staged item, hitting the same create/update endpoints the UI itself calls. Sheets do have
   * a staging table now (`da_dcx_sheet_staging`, populated by Import Events), but still no
   * per-resolution write API, so staged sheet rows ship as a CSV + upload script instead.
   */
  /** Server builds the bundle — POST /v1/dcx-promotion/create-bundle returns the zip, with
   *  one directory per resolved environment. We only send the selected rows. */
  const confirmPr = async () => {
    if (!prTarget) return;
    setIsBuildingBundle(true);

    const resp = await DCXPromotionApis.createBundle(rc, {
      project_id: projectId,
      target_env: prTarget.id,
      attributes: prItems
        .filter((i): i is Extract<PromotionBundleItem, { kind: 'attribute' }> => i.kind === 'attribute')
        .map(({ row, target }) => {
          const src = row.sourceRaw ?? {};
          return {
            status: PRESENCE_TO_STATUS[target.status],
            entityName: src.entityName ?? undefined,
            attributeName: src.attributeName ?? row.attributeId,
            attributeId: row.attributeId,
            attributeValue: target.stagedValue ?? row.sourceValue ?? '',
            userAttributeEvaluatorType: row.userAttributeEvaluatorType ?? undefined,
            description: src.description ?? undefined,
          };
        }),
      clusters: prItems
        .filter((i): i is Extract<PromotionBundleItem, { kind: 'cluster' }> => i.kind === 'cluster')
        .map(({ row, target }) => ({
          status: PRESENCE_TO_STATUS[target.status],
          clusterReference: row.clusterReference,
          evaluationCriteria: target.stagedValue ?? row.sourceValue ?? undefined,
          clusterId: target.clusterId ?? undefined,
          experimentId: resolveExperimentId(experimentsInTarget(clusterRows, target.targetId), row.clusterCategory),
          clusterName: row.clusterName,
          clusterType: row.clusterCategory ?? undefined,
        })),
      sheets: prItems
        .filter((i): i is Extract<PromotionBundleItem, { kind: 'sheet' }> => i.kind === 'sheet')
        .map(({ row }) => {
          const staged = sheetStagedByTarget[prTarget.id] ?? {};
          const key = stagedKeyOf(row.featureId, row.clusterReference);
          return {
            featureId: row.featureId,
            clusterReference: row.clusterReference,
            // A staged "" or null is a deliberate blank cell — only fall back to the live
            // source value when this key isn't staged at all.
            resolution: key in staged ? staged[key] : row.sourceResolution,
            product: row.product,
            level: row.level,
          };
        }),
    });

    setIsBuildingBundle(false);

    if (!resp.success || !resp.data) {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to build the promotion bundle');
      return;
    }

    const { blob, filename } = resp.data;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    setPrTargetId(null);
    toast.success(`Downloaded ${filename}`);
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
      <CardHeader
        sx={{ textAlign: 'left' }}
        title={<Typography sx={{ fontWeight: 700 }}>Configuration Auditor</Typography>}
        subheader={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: 'divider', fontSize: 12, fontWeight: 600 }}>
              {sourceEnvLabel}
            </Box>
            <Typography component="span" sx={{ fontSize: 14, color: 'text.secondary' }}>→</Typography>
            {targets.map((t) => (
              <Box key={t.id} component="span" sx={{ px: 1, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: 'divider', fontSize: 12, fontWeight: 600 }}>
                {t.label}
              </Box>
            ))}
          </Box>
        }
        action={
          <StudioButton variant="outline" onClick={onBack} startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}>
            Back to Selection
          </StudioButton>
        }
      />
      <CardContent>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 1, '& .MuiTab-root': { textTransform: 'uppercase', fontWeight: 700, fontSize: 12 }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' } }}
        >
          <Tab value="attributes" label="Attributes" />
          <Tab value="clusters" label="Cluster Inventory" />
          <Tab value="sheets" label="DCX Matrix Audit" />
        </Tabs>
        <Divider sx={{ mb: 2 }} />

        {tab === 'attributes' && (
          <AttributesTab
            rows={attributeRows}
            isLoading={isLoadingAttributes}
            onlyDifferences={onlyDifferences}
            setOnlyDifferences={setOnlyDifferences}
            selected={attributeSelected}
            setSelected={setAttributeSelected}
            onStage={promoteAttribute}
            onUnstage={unstageAttribute}
            targets={targets}
            showSelection={stageTargets.length > 0}
            sourceEnvType={sourceEnvType}
          />
        )}
        {tab === 'clusters' && (
          <ClusterInventoryTab
            rows={clusterRows}
            isLoading={isLoadingClusters}
            onlyDifferences={onlyDifferences}
            setOnlyDifferences={setOnlyDifferences}
            selected={clusterSelected}
            setSelected={setClusterSelected}
            onStage={promoteCluster}
            onUnstage={unstageCluster}
            targets={targets}
            showSelection={stageTargets.length > 0}
            sourceEnvType={sourceEnvType}
          />
        )}
        {tab === 'sheets' && (
          <SheetsTab
            rows={sheetRows}
            isLoading={isLoadingSheets}
            onlyDifferences={onlyDifferences}
            setOnlyDifferences={setOnlyDifferences}
            targets={targets}
            sourceEnvLabel={sourceEnvLabel}
            sourceEnvType={sourceEnvType}
            showSelection={stageTargets.length > 0}
            selected={sheetSelected}
            setSelected={setSheetSelected}
            stagedByTarget={sheetStagedByTarget}
          />
        )}

        {stageTargets.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, fontStyle: 'italic', color: 'text.secondary' }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                Checked items will be bundled into a consolidated promotion request.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {stageTargets.map((t) => (
                  <StudioButton
                    key={t.id}
                    variant={stageTargets.length > 1 ? 'outline' : 'primary'}
                    disabled={(bundleByTarget.get(t.id)?.length ?? 0) === 0}
                    onClick={() => setPrTargetId(t.id)}
                  >
                    Create {t.label} PR ({bundleByTarget.get(t.id)?.length ?? 0})
                  </StudioButton>
                ))}
              </Box>
            </Box>
          </>
        )}
      </CardContent>

      <Dialog open={prTargetId !== null} onClose={() => setPrTargetId(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Promote staged changes to {prTarget?.label}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Review the {prItems.length} item{prItems.length === 1 ? '' : 's'} that will be included in this bundle.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', p: 1.5, mb: 2, borderRadius: 1, bgcolor: 'action.hover', fontWeight: 600 }}>
            Downloads a .zip of runnable curl scripts, one directory per environment. Sheets have no
            per-resolution write API, so those ship as a CSV plus an `aws s3 cp` + upload script — running them
            needs awscli configured for the target account.
          </Typography>
          <Box sx={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {prItems.map((item, idx) => (
              <Box
                key={idx}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    {item.kind === 'attribute'
                      ? item.row.attributeId
                      : item.kind === 'cluster'
                        ? item.row.clusterName
                        : `${sheetRowLabel(item.row)} · ${item.row.clusterReference}`}
                  </Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary' }}>
                    {item.kind === 'attribute' ? 'Attribute' : item.kind === 'cluster' ? 'Cluster' : 'Sheet (CSV only)'}
                    {item.kind !== 'sheet' && ` · ${item.target.status === 'Not present' ? 'create' : 'update'}`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <StudioButton variant="outline" onClick={() => setPrTargetId(null)}>Cancel</StudioButton>
          <StudioButton variant="primary" disabled={isBuildingBundle} onClick={confirmPr}>
            {isBuildingBundle ? 'Building…' : 'Download Bundle'}
          </StudioButton>
        </DialogActions>
      </Dialog>

    </Card>
  );
}
